import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductsTable = ({ products, onRowExpand, onReviewClick, onExportClick }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const toggleRowExpansion = (productId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(productId)) {
      newExpanded?.delete(productId);
    } else {
      newExpanded?.add(productId);
    }
    setExpandedRows(newExpanded);
    if (onRowExpand) onRowExpand(productId, !expandedRows?.has(productId));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getMatchTypeConfig = (matchType) => {
    switch (matchType) {
      case 'exact_ean':
        return { 
          label: 'Exact EAN', 
          color: 'text-success', 
          bgColor: 'bg-success bg-opacity-10',
          icon: 'CheckCircle'
        };
      case 'exact_mpn':
        return { 
          label: 'Exact MPN', 
          color: 'text-success', 
          bgColor: 'bg-success bg-opacity-10',
          icon: 'CheckCircle'
        };
      case 'ai_fuzzy':
        return { 
          label: 'AI Match', 
          color: 'text-warning', 
          bgColor: 'bg-warning bg-opacity-10',
          icon: 'Zap'
        };
      case 'ambiguous':
        return { 
          label: 'Needs Review', 
          color: 'text-error', 
          bgColor: 'bg-error bg-opacity-10',
          icon: 'AlertTriangle'
        };
      default:
        return { 
          label: 'Unknown', 
          color: 'text-muted-foreground', 
          bgColor: 'bg-muted',
          icon: 'HelpCircle'
        };
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left">
                <div className="w-6"></div>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('productName')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Product</span>
                  <Icon name={getSortIcon('productName')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('brand')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Brand</span>
                  <Icon name={getSortIcon('brand')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('bestPrice')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Best Price</span>
                  <Icon name={getSortIcon('bestPrice')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-medium text-foreground">Match Type</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('confidence')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Confidence</span>
                  <Icon name={getSortIcon('confidence')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-medium text-foreground">Suppliers</span>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {products?.map((product) => {
              const matchConfig = getMatchTypeConfig(product?.matchType);
              const isExpanded = expandedRows?.has(product?.id);
              
              return (
                <React.Fragment key={product?.id}>
                  {/* Main Row */}
                  <tr className="hover:bg-muted hover:bg-opacity-50 transition-colors">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleRowExpansion(product?.id)}
                        className="p-1 hover:bg-muted rounded transition-colors"
                      >
                        <Icon 
                          name={isExpanded ? 'ChevronDown' : 'ChevronRight'} 
                          size={16} 
                          className="text-muted-foreground"
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-foreground">{product?.productName}</p>
                        {product?.mpn && (
                          <p className="text-xs text-muted-foreground">MPN: {product?.mpn}</p>
                        )}
                        {product?.ean && (
                          <p className="text-xs text-muted-foreground">EAN: {product?.ean}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">{product?.brand}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-foreground">
                          {formatCurrency(product?.bestPrice)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          by {product?.bestPriceSupplier}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${matchConfig?.bgColor} ${matchConfig?.color}`}>
                        <Icon name={matchConfig?.icon} size={12} />
                        <span>{matchConfig?.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-300 ${
                              product?.confidence >= 0.9 ? 'bg-success' : 
                              product?.confidence >= 0.8 ? 'bg-warning' : 'bg-error'
                            }`}
                            style={{ width: `${product?.confidence * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">
                          {Math.round(product?.confidence * 100)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <span className="text-sm text-foreground">{product?.supplierCount}</span>
                        <Icon name="Building2" size={14} className="text-muted-foreground" />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        {product?.matchType === 'ambiguous' && (
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={() => onReviewClick && onReviewClick(product?.id)}
                            iconName="Eye"
                            className="text-xs"
                          >
                            Review
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onExportClick && onExportClick(product?.id)}
                          iconName="Download"
                          className="h-8 w-8"
                        />
                      </div>
                    </td>
                  </tr>
                  {/* Expanded Row */}
                  {isExpanded && (
                    <tr>
                      <td colSpan="8" className="px-4 py-0">
                        <div className="bg-muted bg-opacity-30 p-4 border-l-4 border-primary">
                          <h4 className="text-sm font-medium text-foreground mb-3">
                            All Supplier Comparisons
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {product?.allSuppliers?.map((supplier, index) => (
                              <div 
                                key={index}
                                className={`p-3 rounded-lg border ${
                                  supplier?.isBestPrice ? 'border-success bg-success bg-opacity-10' : 'border-border bg-card'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-foreground">{supplier?.name}</span>
                                  {supplier?.isBestPrice && (
                                    <div className="flex items-center space-x-1 text-success">
                                      <Icon name="Crown" size={12} />
                                      <span className="text-xs font-medium">Best Price</span>
                                    </div>
                                  )}
                                </div>
                                <div className="space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-xs text-muted-foreground">Price:</span>
                                    <span className="text-sm font-semibold text-foreground">
                                      {formatCurrency(supplier?.price)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-xs text-muted-foreground">Stock:</span>
                                    <span className={`text-xs font-medium ${
                                      supplier?.stock > 0 ? 'text-success' : 'text-error'
                                    }`}>
                                      {supplier?.stock > 0 ? `${supplier?.stock} units` : 'Out of stock'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-xs text-muted-foreground">Updated:</span>
                                    <span className="text-xs text-muted-foreground">
                                      {supplier?.lastUpdated}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductsTable;